/**
 * Which new-type/new-field suggestions this job has paused, and every word that
 * says how to resume them. The persisted stopped-at names remain unchanged.
 *
 * Two of the twelve suggestion kinds ADD TO A JOB'S SHAPE — a document type it
 * does not have, and a field it does not collect. The other ten improve how it
 * reads what it already has, and nobody has ever asked to be offered fewer of
 * those. So there are two switches and no third level: "the whole job" is not a
 * master switch, it is the state both switches produce, and closing the first
 * offers the second on the same receipt.
 *
 * A stance NEVER touches the review policy. Documents the reader is unsure of
 * still arrive in the review queue exactly as before — the operator loses a
 * proposal, never a document. That promise is why the confirm says it out loud
 * and why nothing here reads or writes a review bar.
 *
 * The strings live beside the rule rather than inside the drawer's JSX because
 * they are the feature: four surfaces (the improvements line, the drawer foot,
 * the receipt, job setup) have to say the same thing in the same words, and a
 * fifth (the learning screen) has to admit the same fact. Copy compared against
 * itself in five places drifts; copy read from one place cannot.
 */

/** The job's stance as stored (shared/desktop-api.ts), Calibration's times included (shared/calibration.ts). */
export type LearningStance = import('../shared/desktop-api').LearningStance;

export const STANCE_AREA = { newTypes: 'new-types', newFields: 'new-fields' } as const;
export type StanceArea = (typeof STANCE_AREA)[keyof typeof STANCE_AREA];

/** The two rows, in the order they are always drawn. */
export const STANCE_AREAS: StanceArea[] = [STANCE_AREA.newTypes, STANCE_AREA.newFields];

/**
 * Which half a suggestion kind belongs to, or neither. Mirrors
 * `SuggestionArea.Of` in the engine's Vocabulary.cs, and the two must agree:
 * a kind the engine holds back and this side still counts on the foot's row
 * would print "2 set aside" beside a number nothing can show.
 *
 * It lives here rather than in desktop-api.ts, where the design put it, because
 * this module is deliberately import-free — the copy suite compiles it on its
 * own — and every other rule about the two areas is already in this file.
 */
export const suggestionArea = (kind: string): StanceArea | null =>
  kind === 'document-type'
    ? STANCE_AREA.newTypes
    : kind === 'new-field'
      ? STANCE_AREA.newFields
      : null;

/**
 * What a card of this kind is counted in. "14 confirmations" under a new-field
 * card is a lie — nobody confirmed anything; the documents merely print it.
 */
export const suggestionCountNoun = (kind: string): string =>
  kind === 'document-type'
    ? 'matching document'
    : kind === 'new-field'
      ? 'document'
      : 'confirmation';

export const stanceAreaLabel = (area: StanceArea) =>
  area === STANCE_AREA.newTypes ? 'New document types' : 'New fields';

export const stanceStoppedAt = (stance: LearningStance | null | undefined, area: StanceArea) =>
  (area === STANCE_AREA.newTypes ? stance?.newTypesStoppedAt : stance?.newFieldsStoppedAt) ?? null;

export const stanceStopped = (stance: LearningStance | null | undefined, area: StanceArea) =>
  Boolean(stanceStoppedAt(stance, area));

/** The stance this job would have after one switch is thrown. */
export const withStanceChange = (
  stance: LearningStance | null | undefined,
  area: StanceArea,
  stoppedAt: string | null,
): LearningStance => ({
  ...(stance ?? {}),
  ...(area === STANCE_AREA.newTypes
    ? { newTypesStoppedAt: stoppedAt }
    : { newFieldsStoppedAt: stoppedAt }),
});

/**
 * "12 August" — the day and the month, never the year and never a clock.
 *
 * Composed rather than handed to `toLocaleDateString` with a day/month preset,
 * because that preset answers "August 12" in one locale and "12 August" in the
 * next, and the foot row's shape is part of the approved words. The month name
 * is still the locale's own.
 */
export const stanceDayLabel = (iso: string, locale?: string) => {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '';
  return `${when.getDate()} ${when.toLocaleDateString(locale, { month: 'long' })}`;
};

export type ImprovementsLineCopy = { headline: string; hint: string; stance: boolean };

/**
 * The one line in the results column, which now answers two questions with the
 * same three words of chrome: what is waiting, and — when nothing is — why it
 * has gone quiet.
 *
 * Pending suggestions always win the line. They are work; a stance is a standing
 * fact, and a fact that hides work is a defect.
 */
export const improvementsLineCopy = (
  pendingCount: number,
  stance: LearningStance | null | undefined,
  setAsideCount = 0,
): ImprovementsLineCopy | null => {
  if (pendingCount > 0)
    return {
      headline:
        pendingCount === 1
          ? '1 job adjustment suggested'
          : `${pendingCount} job adjustments suggested`,
      hint: 'tap to review',
      stance: false,
    };
  const types = stanceStopped(stance, STANCE_AREA.newTypes);
  const fields = stanceStopped(stance, STANCE_AREA.newFields);
  if (!types && !fields) return null;
  const subject =
    types && fields
      ? 'New-type and new-field suggestions paused'
      : types
        ? 'New document type suggestions paused'
        : 'New field suggestions paused';
  return {
    headline: setAsideCount > 0 ? `${subject} · ${setAsideCount} set aside` : subject,
    hint: 'tap to change',
    stance: true,
  };
};

export const STANCE_FOOT_HEADING = 'Suggestions for this job';
/** Said once, under both rows: what a stance can never reach. */
export const STANCE_FOOT_SENTENCE =
  'Only these suggestions pause. Reading, review and other improvements continue.';
export const STANCE_SETUP_HEADING = 'Suggestions';
export const STANCE_SETUP_SUBHEADING = 'New types and fields for this job. Resume any time.';

export type StanceRowCopy = {
  area: StanceArea;
  label: string;
  state: string;
  action: string;
  stopped: boolean;
  setAsideCount: number;
};

/** One row per area: the label, one state word carrying it at a glance, one button. */
export const stanceFootRows = (
  stance: LearningStance | null | undefined,
  setAside: Partial<Record<StanceArea, number>> = {},
  locale?: string,
): StanceRowCopy[] =>
  STANCE_AREAS.map((area) => {
    const stoppedAt = stanceStoppedAt(stance, area);
    const held = setAside[area] ?? 0;
    const state = stoppedAt
      ? `Paused since ${stanceDayLabel(stoppedAt, locale)}${held > 0 ? ` · ${held} set aside` : ''}`
      : 'Suggesting';
    return {
      area,
      label: stanceAreaLabel(area),
      state,
      action: stoppedAt ? 'Resume suggestions' : 'Pause suggestions',
      stopped: Boolean(stoppedAt),
      setAsideCount: held,
    };
  });

export type StanceConfirmCopy = {
  question: string;
  body: string;
  aside: string;
  confirm: string;
  cancel: string;
};

/**
 * The reason, said ONCE, here, where it is needed — not in the foot rows, not on
 * the line, not in a tooltip. It opens in place in the foot and is never a modal.
 */
export const stanceStopConfirmCopy = (area: StanceArea): StanceConfirmCopy =>
  area === STANCE_AREA.newTypes
    ? {
        question: 'Pause suggestions for new document types?',
        body: 'Hide new-type suggestions for this job. Reading, review and improvements to existing types continue.',
        aside: 'Resume suggestions here any time. You can still add a type yourself.',
        confirm: 'Pause suggestions',
        cancel: 'Keep suggesting',
      }
    : {
        question: 'Pause suggestions for new fields?',
        body: 'Hide new-field suggestions for this job. Reading, review and improvements to existing fields continue.',
        aside: 'Resume suggestions here any time. You can still add a field yourself.',
        confirm: 'Pause suggestions',
        cancel: 'Keep suggesting',
      };

export type StanceReceiptCopy = { title: string; detail: string | null; chain: StanceArea | null };

/**
 * What the receipt says after a switch was thrown, and whether it offers the
 * other one.
 *
 * The chain is capped at two receipts, never three: stopping types offers
 * "Fields too", and the receipt that follows THAT offers nothing.
 */
export const stanceReceiptCopy = (
  stance: LearningStance | null | undefined,
  area: StanceArea,
  action: 'stopped' | 'resumed',
  setAsideCount = 0,
): StanceReceiptCopy => {
  const noun = area === STANCE_AREA.newTypes ? 'new document types' : 'new fields';
  if (action === 'resumed')
    return { title: `Suggestions for ${noun} resumed.`, detail: null, chain: null };
  const both = STANCE_AREAS.every((item) => stanceStopped(stance, item));
  if (both)
    return {
      title: 'New-type and new-field suggestions paused.',
      detail: 'Resume either here any time. Reading, review and other improvements continue.',
      chain: null,
    };
  if (setAsideCount > 0)
    return {
      title: `Suggestions for ${noun} paused · ${setAsideCount} proposals set aside`,
      detail: 'Resume suggestions here any time.',
      chain: null,
    };
  return {
    title: `Suggestions for ${noun} paused.`,
    detail: 'Resume suggestions here any time. You can still add one yourself.',
    chain: area === STANCE_AREA.newTypes ? STANCE_AREA.newFields : null,
  };
};

/** The button on the chained receipt, and the button an apply receipt offers. */
export const stanceOfferLabel = (area: StanceArea) =>
  area === STANCE_AREA.newTypes
    ? 'Pause new document type suggestions'
    : 'Pause new field suggestions';
export const STANCE_CHAIN_LABEL = 'Pause fields too';

/**
 * A screen headed *Learning* that is silently not learning something is the
 * exact defect this feature would otherwise introduce. When both are stopped the
 * date is the earlier one, because that is when the quiet actually started.
 */
export const learningScreenStanceLine = (
  stance: LearningStance | null | undefined,
  locale?: string,
) => {
  const types = stanceStoppedAt(stance, STANCE_AREA.newTypes);
  const fields = stanceStoppedAt(stance, STANCE_AREA.newFields);
  if (!types && !fields) return null;
  const since =
    types && fields ? (new Date(types) <= new Date(fields) ? types : fields) : (types ?? fields)!;
  const subject =
    types && fields ? 'New types and fields' : types ? 'New document types' : 'New fields';
  return `${subject} · suggestions paused since ${stanceDayLabel(since, locale)}`;
};

/** Thirty days: the difference between an offer and a nag. */
export const STANCE_OFFER_DECLINE_DAYS = 30;

/**
 * Whether an apply receipt may carry the offer.
 *
 * The maturity rule: the moment an operator learns Paige can find a type
 * is the worst possible moment to offer to switch that off, so the job's FIRST
 * acceptance never raises it. A dismissal is a decline, and a decline holds the
 * offer off for thirty days.
 */
export const stanceOfferAllowed = (input: {
  stance: LearningStance | null | undefined;
  area: StanceArea;
  acceptedCount: number;
  now: Date;
}) => {
  if (stanceStopped(input.stance, input.area)) return false;
  if (input.acceptedCount < 2) return false;
  const declined = input.stance?.offerDeclinedAt;
  if (!declined) return true;
  const elapsed = input.now.getTime() - new Date(declined).getTime();
  return Number.isNaN(elapsed) || elapsed > STANCE_OFFER_DECLINE_DAYS * 24 * 60 * 60 * 1_000;
};
